/**
 * useRiderWorkStatus Hook
 * 라이더 근무 현황 페이지의 데이터 및 상태 관리 훅
 *
 * @description
 * - 기간별 라이더 근무 현황 조회
 * - 협력사별 라이더 그룹핑
 * - 날짜별 근무 상태 표시
 * - 엑셀 다운로드 기능
 *
 * @returns 근무 현황 데이터 상태 및 관리 함수
 */
import { useState, useEffect, useCallback } from 'react';
import { riderWorkStatusApi } from '../api/RiderWorkStatusApi';
import { RiderWorkStatusFilters, RiderWorkStatusResponseDto, SupplierWorkStatus, SupplierWorkStatusDto, RiderWorkStatusItemDto } from '../types/riderWorkStatus.types';
import { getItem } from '@/shared/utils/storage';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';

/**
 * 날짜를 YYYYMMDD 형식으로 변환 (API 호출용)
 * 필터에 저장된 날짜가 이미 YYYYMMDD 형식이면 그대로 사용
 */
const formatDateToYYYYMMDD = (date: string): string => {
    if (!date) return '';
    // 이미 YYYYMMDD 형식이면 그대로 반환
    if (date.length === 8 && !date.includes('-')) {
        return date;
    }
    // YYYY-MM-DD 형식이면 YYYYMMDD로 변환
    return date.replace(/-/g, '');
};

/**
 * 기본 필터 생성 (오늘 기준 7일 전 ~ 오늘)
 */
const createDefaultFilters = (): RiderWorkStatusFilters => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    return {
        fromDate: formatDate(sevenDaysAgo),
        toDate: formatDate(today)
    };
};

export const useRiderWorkStatus = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [excelDownloading, setExcelDownloading] = useState(false);
    const [workStatusData, setWorkStatusData] = useState<SupplierWorkStatus[]>([]);
    const [filters, setFilters] = useState<RiderWorkStatusFilters>(() => {
        // 필터 저장하지 않고 항상 기본값 사용
        return createDefaultFilters();
    });

    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');
    const isSupplierUser = userType === 'SUPPLIER';

    /**
     * 라이더 근무 현황 조회
     */
    const fetchWorkStatus = useCallback(async (customFilters?: RiderWorkStatusFilters) => {
        try {
            setLoading(true);
            const filtersToUse = customFilters || filters;
            
            // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
            const fromDate = formatDateToYYYYMMDD(filtersToUse.fromDate);
            const toDate = formatDateToYYYYMMDD(filtersToUse.toDate);
            
            if (!fromDate || !toDate) {
                showToast('시작일과 종료일을 입력해주세요.', { type: ToastType.WARNING });
                return;
            }

            // SUPPLIER 사용자는 자동으로 suppId 설정
            let suppId = filtersToUse.suppId;
            if (isSupplierUser && userId) {
                suppId = userId;
            }

            const response = await riderWorkStatusApi.getRiderWorkStatus(fromDate, toDate, suppId);
            
            // 응답 데이터 처리 및 매핑
            let suppliersData: SupplierWorkStatus[] = [];
            if (response && typeof response === 'object') {
                const responseData = response as RiderWorkStatusResponseDto;
                let rawSuppliers: SupplierWorkStatusDto[] = [];
                
                if (responseData.suppliers && Array.isArray(responseData.suppliers)) {
                    rawSuppliers = responseData.suppliers;
                } else if ('data' in response) {
                    const dataResponse = response as { data?: RiderWorkStatusResponseDto };
                    if (dataResponse.data?.suppliers && Array.isArray(dataResponse.data.suppliers)) {
                        rawSuppliers = dataResponse.data.suppliers;
                    }
                }
                
                // deliveryAmtSum을 deliveryFeeTotal로 매핑
                suppliersData = rawSuppliers.map((supplier: SupplierWorkStatusDto) => ({
                    suppId: supplier.suppId,
                    suppName: supplier.suppName,
                    riderCount: supplier.riderCount,
                    riders: supplier.riders.map((rider: RiderWorkStatusItemDto) => ({
                        riderId: rider.riderId,
                        riderName: rider.riderName,
                        totalWorkDays: rider.totalWorkDays,
                        workStatusByDate: rider.workStatusByDate,
                        deliveryFeeTotal: rider.deliveryAmtSum,
                        totalDeliveryCnt: rider.totalDeliveryCnt
                    }))
                }));
            }

            setWorkStatusData(suppliersData);
        } catch {
            showToast('라이더 근무 현황을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setWorkStatusData([]);
        } finally {
            setLoading(false);
        }
    }, [filters, isSupplierUser, userId, showToast]);

    /**
     * 필터 변경 핸들러
     */
    const handleFilterChange = useCallback((key: keyof RiderWorkStatusFilters, value: string) => {
        setFilters((prev: RiderWorkStatusFilters) => {
            // 필터 저장하지 않음
            return { ...prev, [key]: value };
        });
    }, []);

    /**
     * 검색 실행
     */
    const handleSearch = useCallback(() => {
        fetchWorkStatus();
    }, [fetchWorkStatus]);

    /**
     * 엑셀 다운로드
     */
    const handleExcelDownload = useCallback(async () => {
        try {
            setExcelDownloading(true);
            // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
            const fromDate = formatDateToYYYYMMDD(filters.fromDate);
            const toDate = formatDateToYYYYMMDD(filters.toDate);
            
            if (!fromDate || !toDate) {
                showToast('시작일과 종료일을 입력해주세요.', { type: ToastType.WARNING });
                return;
            }

            // SUPPLIER 사용자는 자동으로 suppId 설정
            let suppId = filters.suppId;
            if (isSupplierUser && userId) {
                suppId = userId;
            }

            // 최소 근무일수 파싱
            const minWorkDays = filters.minWorkDays ? parseInt(filters.minWorkDays, 10) : undefined;
            const blob = await riderWorkStatusApi.downloadWorkStatusExcel(fromDate, toDate, suppId, minWorkDays);
            
            // 파일 다운로드
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // 파일명 생성 (YYYYMMDD~YYYYMMDD_라이더근무현황.xlsx)
            link.download = `${fromDate}~${toDate}_라이더근무현황.xlsx`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // 메모리 해제
            window.URL.revokeObjectURL(downloadUrl);
            
            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 파일 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setExcelDownloading(false);
        }
    }, [filters, isSupplierUser, userId, showToast]);

    /**
     * 초기 데이터 로드
     */
    useEffect(() => {
        // 필터 저장하지 않고 항상 기본값 사용
        const filtersToUse = createDefaultFilters();
        fetchWorkStatus(filtersToUse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        loading,
        excelDownloading,
        workStatusData,
        filters,
        isSupplierUser,
        handleFilterChange,
        handleSearch,
        handleExcelDownload,
        fetchWorkStatus
    };
};

